import { useEffect, useRef, useState } from "react";
import { ChevronDown, Check, Search } from "lucide-react";
import { groupedProducts, PRODUCTS_BY_SLUG } from "@/lib/productCatalog";

/**
 * Animated grouped dropdown for picking a clinical product.
 * Smooth slide-in panel, category headers, fuzzy search, keyboard support (esc to close).
 *
 * Props:
 *   value     - selected slug (string)
 *   onChange  - (slug) => void
 *   testid    - data-testid prefix (default "product-select")
 */
export default function ProductSelect({ value, onChange, testid = "product-select" }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const wrapRef = useRef(null);
    const inputRef = useRef(null);

    const selected = PRODUCTS_BY_SLUG[value] || null;
    const groups = groupedProducts();
    const filtered = groups
        .map((g) => ({
            ...g,
            items: g.items.filter((p) =>
                !q ||
                p.name.toLowerCase().includes(q.toLowerCase()) ||
                p.slug.toLowerCase().includes(q.toLowerCase()) ||
                p.hint.toLowerCase().includes(q.toLowerCase())
            ),
        }))
        .filter((g) => g.items.length > 0);

    useEffect(() => {
        if (!open) return;
        const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onClick);
        document.addEventListener("keydown", onKey);
        setTimeout(() => inputRef.current?.focus(), 60);
        return () => {
            document.removeEventListener("mousedown", onClick);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const choose = (slug) => {
        onChange(slug);
        setOpen(false);
        setQ("");
    };

    return (
        <div ref={wrapRef} className="relative">
            <button
                type="button"
                data-testid={`${testid}-trigger`}
                onClick={() => setOpen((v) => !v)}
                className={`tr-input flex items-center justify-between gap-3 text-left w-full transition-colors ${open ? "ring-2 ring-cyan-400 border-cyan-400" : ""}`}
            >
                <div className="min-w-0">
                    {selected ? (
                        <>
                            <div className="text-[14.5px] font-medium text-slate-900 truncate">{selected.name}</div>
                            <div className="text-[11.5px] text-slate-500 mt-0.5 truncate">{selected.hint}</div>
                        </>
                    ) : (
                        <div className="text-[14px] text-slate-400">Choose a clinical product…</div>
                    )}
                </div>
                <ChevronDown size={16} className={`shrink-0 text-slate-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <div
                    data-testid={`${testid}-panel`}
                    className="absolute z-40 left-0 right-0 mt-2 rounded-2xl border border-slate-200 bg-white shadow-[0_24px_60px_-20px_rgba(8,47,73,0.25)] overflow-hidden origin-top"
                    style={{
                        animation: "tr-pop-in 180ms cubic-bezier(0.22, 1, 0.36, 1) both",
                    }}
                >
                    <style>{`@keyframes tr-pop-in {
                        from { opacity: 0; transform: translateY(-6px) scale(0.98); }
                        to   { opacity: 1; transform: translateY(0)    scale(1); }
                    }`}</style>

                    <div className="border-b border-slate-100 px-4 py-2.5 flex items-center gap-2">
                        <Search size={15} className="text-slate-400" />
                        <input
                            ref={inputRef}
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            data-testid={`${testid}-search`}
                            placeholder="Search products…"
                            className="flex-1 bg-transparent outline-none text-[14px]"
                        />
                    </div>

                    <div className="max-h-[420px] overflow-y-auto">
                        {filtered.length === 0 ? (
                            <div className="px-4 py-8 text-center text-[13px] text-slate-500">No products match “{q}”.</div>
                        ) : (
                            filtered.map((g, gi) => (
                                <div key={g.id} className="py-2" style={{ animationDelay: `${gi * 40}ms`, animation: "tr-pop-in 220ms ease both" }}>
                                    <div className="px-4 pb-1.5 font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-slate-400">
                                        {g.label}
                                    </div>
                                    {g.items.map((p) => {
                                        const isSel = p.slug === value;
                                        return (
                                            <button
                                                key={p.slug}
                                                type="button"
                                                data-testid={`${testid}-option-${p.slug}`}
                                                onClick={() => choose(p.slug)}
                                                className={`w-full text-left px-4 py-2.5 flex items-start gap-3 hover:bg-cyan-50/50 transition-colors ${isSel ? "bg-cyan-50/70" : ""}`}
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-[14px] font-medium text-slate-900 flex items-center gap-2">
                                                        {p.name}
                                                        {p.needs === "span" && <span className="px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 text-[9.5px] font-mono-tr tracking-wider uppercase">span</span>}
                                                        {p.needs === "arch" && <span className="px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 text-[9.5px] font-mono-tr tracking-wider uppercase">arch</span>}
                                                        {p.needs === "teeth" && <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[9.5px] font-mono-tr tracking-wider uppercase">teeth</span>}
                                                    </div>
                                                    <div className="text-[12px] text-slate-500 mt-0.5">{p.hint}</div>
                                                </div>
                                                {isSel && <Check size={16} className="text-cyan-600 mt-1 shrink-0" />}
                                            </button>
                                        );
                                    })}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
